import { Tab, TabProps } from '@nexelem/deprecated-theme';
import { cva } from 'class-variance-authority';
import { ForwardRefExoticComponent, SyntheticEvent, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@nexelem/utils';

const detailsTabVariants = cva(
  'box-border inline-flex items-center justify-center gap-2 border-b-transparent normal-case no-underline transition-colors [&_.MuiTab-iconWrapper]:ml-0! [&_svg]:pr-2',
  {
    variants: {
      size: {
        medium: 'h-11 min-h-11 border-b-[2px] px-6 text-t16-sb',
        large: 'h-25 min-h-25 border-b-[4px] px-8 text-t20-sb',
        jumbo: 'h-25 min-h-25 border-b-[4px] px-8 text-t28-button',
      },
      hasError: {
        true: 'text-text-status-red-lighter hover:border-border-status-red-dark active:border-border-status-red-dark active:text-text-status-red-lighter',
        false: 'text-text-action hover:border-border-hover active:border-border-focus active:text-text-focus',
      },
    },
    defaultVariants: {
      size: 'medium',
      hasError: false,
    },
  },
);

export type DetailsTabProps = Omit<TabProps, 'onClick'> & {
  to?: string;
  component?: ForwardRefExoticComponent<any>;
} & {
  readonly onClick?: (value: string) => TabProps['onClick'];
  readonly size?: 'jumbo' | 'large' | 'medium';
  readonly hasError?: boolean;
};

export const DetailsTab = ({
  sx,
  value,
  onClick: onClickProps,
  hasError = false,
  size = 'medium',
  className,
  ...props
}: DetailsTabProps) => {
  const location = useLocation();
  const internalOnClick = useCallback(
    (path?: string) => (evt: SyntheticEvent) => {
      const lastPathElement = location.pathname.split('/').slice(-1).join('');
      if (path && path === lastPathElement) {
        evt.stopPropagation();
        evt.preventDefault();
      }
    },
    [location.pathname],
  );

  const onClick = useMemo(
    () => (onClickProps ? onClickProps(value) : internalOnClick(props.to)),
    [internalOnClick, onClickProps, props.to, value],
  );

  return (
    <Tab
      {...props}
      disableRipple
      sx={sx}
      value={value}
      className={cn(
        detailsTabVariants({ size, hasError }),
        hasError ? 'hasError' : '',
        hasError
          ? '[&.Mui-selected]:!border-border-status-red-dark [&.Mui-selected]:!text-text-status-red-lighter [&.Mui-selected_svg]:!text-text-status-red-lighter'
          : '[&.Mui-selected]:!border-border-brand-darker [&.Mui-selected]:!text-text-brand-darker [&.Mui-selected_svg]:!text-text-brand-darker',
        className,
      )}
      onClick={onClick}
    />
  );
};
